import classNames from 'classnames';
import { Reorder, useDragControls } from 'motion/react';
import React, { useRef, useState } from 'react';
import * as styles from './TopicReorderablePosts.module.scss';
import SVGIcon from '../../components/SVGIcon';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';
import dragHandle from '../../icons/non-prefilled/16x16/drag.svg';

const POST_EDGES_FRAGMENT = gql(`
  fragment TopicReorderablePosts_postEdges on TopicPostEdge {
    node {
      id
    }
    pinnedOrder
  }
`);

type PostEdgesFragment = DocumentType<typeof POST_EDGES_FRAGMENT>;

interface TopicReorderablePostsProps<P extends PostEdgesFragment> {
  postEdges: P[];
  orderPost(id: string, newOrder: number): Promise<unknown>;
  renderPostEdge(postEdge: P, index: number): JSX.Element;
}

const TopicReorderablePosts = <P extends PostEdgesFragment>({
  postEdges,
  orderPost,
  renderPostEdge,
}: TopicReorderablePostsProps<P>) => {
  const [draggingPostId, setDraggingPostId] = useState<string | null>(null);
  const [localPostEdges, setLocalPostEdges] = useState<P[] | null>(null);
  const sourceIndexRef = useRef<number | null>(null);

  const handleDragStart = (postId: string, index: number) => {
    setLocalPostEdges(postEdges);
    setDraggingPostId(postId);
    sourceIndexRef.current = index;
  };

  const handleDragEnd = (postId: string) => {
    if (!localPostEdges) return;

    const sourceIndex = sourceIndexRef.current;
    setDraggingPostId(null);
    sourceIndexRef.current = null;
    if (sourceIndex == null) return;

    const destinationIndex = localPostEdges.findIndex(
      (edge) => edge.node?.id === postId,
    );
    if (destinationIndex === -1 || destinationIndex === sourceIndex) return;

    const newPostEdges = [...postEdges];
    const [edge] = newPostEdges.splice(sourceIndex, 1);

    const newOrder = (() => {
      const postAtNewIndex = newPostEdges[destinationIndex];
      if (postAtNewIndex) {
        return postAtNewIndex.pinnedOrder;
      }

      const postAtLastIndex = newPostEdges.at(-1);
      if (postAtLastIndex?.pinnedOrder != null) {
        return postAtLastIndex.pinnedOrder + 1;
      }

      return undefined;
    })();

    if (edge?.node && newOrder != null) {
      newPostEdges.splice(destinationIndex, 0, edge);

      orderPost(edge.node.id, newOrder).then(() => {
        setLocalPostEdges(null);
      });
    }
  };

  const edges = localPostEdges ?? postEdges;

  return (
    <Reorder.Group
      axis="y"
      values={edges}
      onReorder={setLocalPostEdges}
      as="div"
    >
      {edges.map((edge, index) => (
        <TopicReorderablePostsRow
          key={edge.node?.id ?? String(index)}
          edge={edge}
          index={index}
          draggingPostId={draggingPostId}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          renderPostEdge={renderPostEdge}
        />
      ))}
    </Reorder.Group>
  );
};

interface RowProps<P extends PostEdgesFragment> {
  edge: P;
  index: number;
  draggingPostId: string | null;
  onDragStart: (postId: string, index: number) => void;
  onDragEnd: (postId: string) => void;
  renderPostEdge: (postEdge: P, index: number) => JSX.Element;
}

const TopicReorderablePostsRow = <P extends PostEdgesFragment>({
  edge,
  index,
  draggingPostId,
  onDragStart,
  onDragEnd,
  renderPostEdge,
}: RowProps<P>) => {
  const dragControls = useDragControls();
  const suppressClickRef = useRef(false);
  const post = edge.node;
  if (post == null) return null;

  return (
    <Reorder.Item
      key={post.id}
      value={edge}
      as="div"
      layout="position"
      dragControls={dragControls}
      dragListener={false}
      onPointerDown={(e) => {
        if (
          e.defaultPrevented ||
          e.button !== 0 ||
          e.ctrlKey ||
          e.metaKey ||
          e.shiftKey ||
          e.altKey
        ) {
          return;
        }

        // prevent text selection when initiating drag anywhere on the row
        e.preventDefault();
        dragControls.start(e, {
          // a small threshold to prevent interfering the click event
          distanceThreshold: 5,
        });
      }}
      className={classNames(styles.draggableContainer, {
        // :hover is not true for item that is being active dragged,
        // while dragging, :hover could be triggered on other elements,
        // need extra classes to control the states.
        [styles.draggable]: !draggingPostId,
        [styles.dragging]: draggingPostId === post.id,
      })}
      onDragStart={() => {
        suppressClickRef.current = true;
        onDragStart(post.id, index);
      }}
      onDragEnd={() => onDragEnd(post.id)}
      onClickCapture={(e) => {
        if (suppressClickRef.current) {
          e.preventDefault();
          suppressClickRef.current = false;
        }
      }}
    >
      <div className={styles.dragHandleContainer}>
        <SVGIcon>{dragHandle}</SVGIcon>
      </div>
      {renderPostEdge(edge, index)}
    </Reorder.Item>
  );
};

export default TopicReorderablePosts;
